import type { ReactElement } from 'react'
import { LoadingButton } from '@mui/lab'
import { Box, CircularProgress, IconButton, Tooltip, useTheme } from '@mui/material'
import { MaterialSymbol } from '@ui2/icons'
import { StickyFooter } from '@ui2/structure'

export type Results2ActionBarStatus = 'saving' | 'dirty' | 'invalid' | 'saved'

interface Props {
  status: Results2ActionBarStatus
  statusLabel: string
  saveDisabled: boolean
  revertDisabled: boolean
  /** `submit` makes "Speichern" submit the surrounding form (`onSave` still runs on click). */
  saveType?: 'button' | 'submit'
  onSave?: () => void
  onRevert: () => void
  saveDataCy?: string
  revertDataCy?: string
}

/**
 * Footer shared by every Results2 detail pane: the save status (icon + message) on the left,
 * an icon-only "Rückgängig" and "Speichern" on the right.
 */
const Results2ActionBar = ({
  status,
  statusLabel,
  saveDisabled,
  revertDisabled,
  saveType = 'button',
  onSave,
  onRevert,
  saveDataCy,
  revertDataCy,
}: Props): ReactElement => {
  const theme = useTheme()

  const statusIcon =
    status === 'saving' ? (
      <CircularProgress
        size={14}
        thickness={5}
        sx={{ color: theme.palette.primary.main }}
        aria-hidden
      />
    ) : status === 'dirty' ? (
      <MaterialSymbol icon="edit_note" size={14} sx={{ color: theme.palette.warning.main }} />
    ) : status === 'invalid' ? (
      <MaterialSymbol icon="error" filled size={14} sx={{ color: theme.palette.error.main }} />
    ) : (
      <MaterialSymbol
        icon="check_circle"
        filled
        size={14}
        sx={{ color: theme.palette.success.main }}
      />
    )

  return (
    <StickyFooter>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            fontSize: 11,
            color: status === 'invalid' ? 'error.main' : 'text.secondary',
            fontFamily: 'Roboto',
          }}
        >
          {statusIcon}
          <span>{statusLabel}</span>
        </Box>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          <Tooltip title="Rückgängig" enterDelay={1000} enterNextDelay={1000}>
            {/* A disabled button fires no hover events — the span keeps the tooltip working. */}
            <span>
              <IconButton
                size="small"
                onClick={onRevert}
                disabled={revertDisabled}
                aria-label="Rückgängig"
                data-cy={revertDataCy}
              >
                <MaterialSymbol icon="undo" size={18} />
              </IconButton>
            </span>
          </Tooltip>

          <LoadingButton
            size="small"
            variant="contained"
            type={saveType}
            onClick={onSave}
            disabled={saveDisabled}
            loading={status === 'saving'}
            data-cy={saveDataCy}
          >
            Speichern
          </LoadingButton>
        </Box>
      </Box>
    </StickyFooter>
  )
}

export default Results2ActionBar
